import { HorizontalOriginOption, Overlay, TabStyles, VerticalOriginOption } from '@tableau/widgets';
import React from 'react';

import { NavStyles } from './NavigationStyles';
import { NavListItemState } from './NavList';
import { NavPanelDisplayMode } from './NavPanel';

export interface NavLinkProps {
  readonly activeIconImagePath: string;
  readonly name: string;
  readonly href: string;
  readonly iconImagePath: string;
  readonly isCurrentLink: boolean;
  readonly navPanelDisplayMode: NavPanelDisplayMode;
  readonly testId: string; // we take this in so we can keep the test ids the same as the old top bar
}

export interface NavListLinkProps extends NavListItemState, NavLinkProps {}

export interface NavListLinkState {
  readonly hover: boolean;
}

export class NavListLink extends React.PureComponent<NavListLinkProps, NavListLinkState> {
  private tooltipAnchorElement: HTMLElement;

  public constructor(props: NavListLinkProps) {
    super(props);

    this.state = {hover: false};
  }

  public render(): JSX.Element {
    const showTooltip = this.props.navPanelDisplayMode === 'icon' && (this.state.hover || this.props.focus);
    return (
      <>
        {this.getNavListLinkElement()}
        {showTooltip && this.renderTooltip()}
      </>
    );
  }

  private getTooltipAnchorClientRect = (): ClientRect => {
    if (this.tooltipAnchorElement) {
      const clientRect = this.tooltipAnchorElement.getBoundingClientRect();
      return {
        left: clientRect.left + 6,
        right: clientRect.right + 6,
        top: clientRect.top,
        bottom: clientRect.bottom,
      } as ClientRect;
    }

    return {} as ClientRect;
  }

  private renderTooltip() {
    // Using a custom overlay for tooltip here instead of SharedWidgets.Tooltip
    // because it adds a tabindex=0 which messes with our keyboard navigation
    const contentWrapperStyle: React.CSSProperties = {
      maxWidth: 300,
      backgroundColor: TabStyles.Colors.White,
      borderColor: TabStyles.Colors.L5,
      borderRadius: TabStyles.Sizing.BorderRadius,
      borderStyle: 'solid',
      borderWidth: TabStyles.Sizing.BorderWidth,
      boxShadow: TabStyles.Shadow.Standard,
      boxSizing: 'border-box',
      display: 'block',
      fontSize: TabStyles.Typography.Sizing.Default,
      lineHeight: 'normal',
      padding: 12,
    };

    return (
      <Overlay
        allowAutoPosition
        anchorOrigin={{ horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.centerY }}
        disablePointerEvents={true}
        getAnchorClientRect={this.getTooltipAnchorClientRect}
        stackedOrigin={{ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.centerY }}
      >
        <div
          data-tb-test-id={`${this.props.testId}-tooltip`}
          role="tooltip"
          style={contentWrapperStyle}
        >
          {this.getTooltipContent()}
        </div>
      </Overlay>
    );
  }

  private isCurrentOrHoverOrFocus = () => {
    return this.state.hover || this.props.isCurrentLink || this.props.focus;
  }

  private onMouseEnter = () => {
    this.setState({hover: true});
  }

  private onMouseLeave = () => {
    this.setState({hover: false});
  }

  private getNavListLinkElement = (): JSX.Element => {
    const justIcon = (this.props.navPanelDisplayMode === 'icon');
    return (
      <div
        className={NavStyles.NavLinkContainer}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        ref={this.tooltipAnchorRef}
      >
        <a
          href={this.props.href}
          tabIndex={-1}
          className={NavStyles.getNavLinkClassNames(this.props.isCurrentLink, this.state.hover, this.props.focus)}
          data-tb-test-id={this.props.testId}
        >
          {this.props.isCurrentLink && <div style={NavStyles.SelectedBarStyle}/>}
          <img
            alt={this.props.name}
            src={this.isCurrentOrHoverOrFocus() ? this.props.activeIconImagePath : this.props.iconImagePath}
            className={justIcon? NavStyles.NavIcon : NavStyles.NavIconByText}
          />
          {(!justIcon) &&
            <div className={NavStyles.getNavLinkTextClassNames(this.isCurrentOrHoverOrFocus())}>
              {this.props.name}
            </div>
          }
          {this.props.focus && <div data-tb-test-id={'nav-link-focus'} style={NavStyles.focusBoxStyle(this.props.isCurrentLink)} />}
        </a>
      </div>
    );
  }

  private getTooltipContent = (): JSX.Element => {
    return (
      <span style={{fontWeight: 'normal', whiteSpace: 'nowrap'}}>
        {this.props.name}
      </span>
    );
  }

  private tooltipAnchorRef = (element: HTMLElement): void => {
    this.tooltipAnchorElement = element;
  }
}
